import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { bindActionCreators } from '@reduxjs/toolkit';
import { ErrorMessage, space } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { connect } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import type { InputItem } from '../../components/PersonInput';
import { PageCard } from '../../components/Section';
import { SummaryBox } from '../../components/SummaryBox';
import { Button } from '../../components/buttons';
import { PageHeading } from '../../components/page-heading';
import { SectionHeading as Heading } from '../../components/section-heading';
import { WarningBox } from '../../components/warning-box';
import { type Page, pageMap, pageTitleMap } from '../../paths';
import { generatePreviewDocumentUrl } from '../../services';
import type { Dispatch, RootState } from '../../state';
import {
  type EditorReducerState,
  type Person,
  actions,
  thunks as editorThunks,
  selectors,
} from '../../state/editor';
import { thunks as foundingThunks } from '../../state/founding';
import { serializeEditorToFounding } from '../../state/founding/thunks';
import { track } from '../../utils/track';
import {
  type GroupedErrors,
  type ValidationError,
  errorsByPage,
  validateBoardMember,
  validateCeo,
  validateChair,
  validateFormOwner,
  validateFounding,
  validateOwner,
  validateSignatory,
} from '../../utils/validators';
import { nonRetryableErrors } from '../pre-control-feedback';
import { IncompletePersonForm } from './IncompletePersonForm';

const SummaryErrorList = styled.ul`
  padding-left: 2rem;
  padding-bottom: 1rem;
  list-style-type: square;
`;

const SummaryErrorListItem = styled.li`
  margin-bottom: 1rem;
`;

const SummaryPageList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
`;

const PageErrors: React.FC<{
  page: Page;
  groups: GroupedErrors;
}> = props => {
  const { groups, page } = props;
  const errors = groups.get(page);
  if (errors) {
    return (
      <SummaryErrorList>
        {errors.map(error => (
          <SummaryErrorListItem key={error.page + error.elementId}>
            {error.message}
          </SummaryErrorListItem>
        ))}
      </SummaryErrorList>
    );
  } else {
    return null;
  }
};

interface Props {
  editor: EditorReducerState;
  validationErrors: ValidationError[];
  saving: boolean;
  formOwner: Person;
  ceo: Person | undefined;
  peopleOwners: Person[];
  boardMembers: Person[];
  signatories: Person[];
  chair: Person;
  onChange: (person: Person) => void;
  onConfirm: () => void;
  submitEmail: () => void;
}

interface State {
  previewDocumentUrl?: string;
  previewDocumentUrlSuccess: boolean;
  error?: { retryable: boolean; message: string };
}

class BaseStepPage5 extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = { previewDocumentUrlSuccess: true };
  }

  async updateDocumentUrl() {
    if (
      this.props.validationErrors.length === 0 &&
      !hasPeopleErrors(getPeopleErrors(this.props))
    ) {
      try {
        const url = await generatePreviewDocumentUrl(
          serializeEditorToFounding(this.props.editor),
        );
        this.setState({
          previewDocumentUrl: url,
          previewDocumentUrlSuccess: true,
        });
      } catch {
        this.setState({ previewDocumentUrlSuccess: false });
      }
    }
  }

  componentDidMount() {
    this.updateDocumentUrl();
    const { validationErrors } = this.props;
    validationErrors.forEach(error =>
      track('form', 'error', error.elementId, 0, true),
    );
    getPeopleErrors(this.props).forEach(person =>
      Object.entries(person)
        .filter(([_, hasError]) => !hasError)
        .forEach(([fieldName]) => track('form', 'error', fieldName, 0, true)),
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (hasPeopleErrors(getPeopleErrors(prevProps))) {
      this.updateDocumentUrl();
    }
  }

  render() {
    const { validationErrors, onConfirm, saving, onChange, submitEmail } =
      this.props;
    const { previewDocumentUrl, previewDocumentUrlSuccess, error } = this.state;
    const pageErrors = errorsByPage(validationErrors);
    const orderedPages: Page[] = ['step1', 'step3', 'step4'];
    const pagesWithErrors: Page[] = orderedPages.filter(e => pageErrors.has(e));
    const peopleErrors = getPeopleErrors(this.props);
    const hasPeopleWithErrors = hasPeopleErrors(peopleErrors);

    if (error && !error.retryable) {
      return (
        <Navigate
          to={{
            pathname: pageMap.preControlFeedback,
            search: window.location.search,
          }}
          state={{ error: error.message }}
        />
      );
    }

    if (validationErrors.length > 0 || hasPeopleWithErrors) {
      return (
        <>
          <PageCard>
            <PageHeading>Snart ferdig…</PageHeading>
            <p>Men først er det noen felter som mangler:</p>
            <SummaryPageList>
              {pagesWithErrors.map(e => (
                <li key={e}>
                  <Heading>
                    <Link
                      to={{
                        pathname: pageMap[e],
                        search: window.location.search,
                      }}
                    >
                      {pageTitleMap[e]}
                    </Link>
                  </Heading>

                  <PageErrors groups={pageErrors} page={e} />
                </li>
              ))}
            </SummaryPageList>

            {hasPeopleWithErrors && (
              <>
                <Heading>Personer</Heading>
                {[...peopleErrors.entries()].map(([person, inputs]) => (
                  <IncompletePersonForm
                    key={person.id}
                    person={person}
                    inputs={getInvalidFields(inputs)}
                    onChange={onChange}
                  />
                ))}
              </>
            )}
          </PageCard>

          <Button size="large" fullWidth={true} disabled={true}>
            Fortsett
          </Button>
        </>
      );
    }

    return (
      <>
        <PageCard
          css={css`
            margin: 0;
          `}
        >
          <PageHeading>Sjekk nøye før du signerer</PageHeading>

          <SummaryBox />

          <WarningBox css={space([16], 'margin-top')}>
            Er all informasjonen korrekt? Du kan ikke endre på noe av dette
            senere. Da må du starte prosessen på nytt.
          </WarningBox>
        </PageCard>

        {error ? (
          <ErrorMessage
            css={css`
              text-align: center;
            `}
          >
            Noe gikk galt. Prøv igjen!
          </ErrorMessage>
        ) : (
          <p
            // Placeholder
            css={css`
              ::before {
                content: '';
                display: inline-block;
              }
            `}
          ></p>
        )}

        <Button
          size="large"
          fullWidth={true}
          disabled={saving}
          type="button"
          onClick={async () => {
            this.setState({ error: undefined });
            submitEmail();
            try {
              await onConfirm();
            } catch (error) {
              if (error instanceof Error) {
                this.setState({
                  error: {
                    retryable: !nonRetryableErrors.includes(error.message),
                    message: error.message,
                  },
                });
              } else {
                this.setState({ error: { retryable: true, message: '' } });
              }
            }
          }}
        >
          {saving ? 'Lagrer…' : 'Jeg er klar til å signere'}
        </Button>
        <p
          css={css`
            text-align: center;
          `}
        >
          {previewDocumentUrlSuccess ? (
            previewDocumentUrl ? (
              <a target="_blank" href={previewDocumentUrl}>
                Vis stiftelses&shy;dokumentet
              </a>
            ) : (
              <>Lager stiftelses&shy;dokumentet&hellip;</>
            )
          ) : (
            <span
              css={css`
                color: ${colors.wcagGray};
              `}
            >
              Forhåndsvisning av stiftelses&shy;dokumentet er ikke tilgjengelig
            </span>
          )}
        </p>
      </>
    );
  }
}

function getPeopleErrors(props: Props) {
  const { peopleOwners, boardMembers, signatories, ceo, formOwner, chair } =
    props;

  const peopleErrors = new Map<Person, { [field: string]: boolean }>();

  peopleErrors.set(formOwner, validateFormOwner(formOwner));

  for (const person of peopleOwners) {
    const fields = {
      ...peopleErrors.get(person),
      ...validateOwner(person),
    };
    peopleErrors.set(person, fields);
  }

  if (ceo) {
    const fields = {
      ...peopleErrors.get(ceo),
      ...validateCeo(ceo),
    };
    peopleErrors.set(ceo, fields);
  }

  for (const person of boardMembers) {
    const fields = {
      ...peopleErrors.get(person),
      ...validateBoardMember(person),
    };
    peopleErrors.set(person, fields);
  }

  const fields = {
    ...peopleErrors.get(chair),
    ...validateChair(chair),
  };
  peopleErrors.set(chair, fields);

  for (const person of signatories) {
    const fields = {
      ...peopleErrors.get(person),
      ...validateSignatory(person),
    };
    peopleErrors.set(person, fields);
  }

  return peopleErrors;
}

function hasPeopleErrors(
  peopleErrors: Map<Person, { [field: string]: boolean }>,
) {
  for (const fields of peopleErrors.values()) {
    if (Object.values(fields).some(isValid => isValid === false)) {
      return true;
    }
  }
  return false;
}

function getInvalidFields(fields: { [field: string]: boolean }) {
  return Object.entries(fields)
    .filter(([_key, value]) => value === false)
    .map(([key]) => key as InputItem);
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      onConfirm: foundingThunks.confirmThunk,
      onChange: actions.setPersonInfo,
      submitEmail: editorThunks.submitEmail,
    },
    dispatch,
  );
}

function mapStateToProps(state: RootState) {
  return {
    editor: state.editor,
    validationErrors: validateFounding(state),
    saving: state.app.saving,
    formOwner: state.editor.people[state.editor.formOwner],
    ceo: selectors.getCeo(state),
    peopleOwners: selectors.getPeopleOwners(state.editor),
    boardMembers: selectors.getAllBoardMembers(state),
    chair: selectors.getChair(state),
    signatories: selectors.getSignatories(state.editor),
  };
}

const StepPage5 = connect(mapStateToProps, mapDispatchToProps)(BaseStepPage5);

// React.lazy only supports default exports:
// https://reactjs.org/docs/code-splitting.html#named-exports
// eslint-disable-next-line import/no-default-export
export default StepPage5;
